import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import { invalidateFavoritePairings } from '@hungryroot/favorites';
import { Warning } from '@hungryroot/icons';
import { StatusCode } from '@hungryroot/rest';
import { deleteFavoritePairing } from '@hungryroot/rest/api/v2/customers/[id]/favorite_pairings/[pairing_id]/delete';
import { type CustomerFavorites } from '@hungryroot/rest/api/v2/customers/[id]/favorite_pairings/get';
import { getFavoritePairingsQueryKey } from '../../get/get-favorite-pairings';

type Params = {
  customerId: number;
  pairingId: number;
};

export function useDeleteFavoritePairing() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { data: Params }) => {
      const res = await deleteFavoritePairing(params.data);
      if (res.status !== StatusCode.SuccessNoContent) {
        throw res.data;
      }
      return res.data;
    },
    onMutate: async ({ data }) => {
      const { customerId, pairingId: deletedPairing } = data;

      await queryClient.cancelQueries({
        queryKey: getFavoritePairingsQueryKey(customerId),
      });

      const previousFavoritePairings: CustomerFavorites | undefined =
        queryClient.getQueryData(getFavoritePairingsQueryKey(customerId));

      const nextFavoritePairings =
        previousFavoritePairings?.favorite_pairings.filter(
          (obj) => obj.pairing_id !== deletedPairing
        );
      queryClient.setQueryData(getFavoritePairingsQueryKey(customerId), {
        favorite_pairings: nextFavoritePairings,
      });

      return { previousFavoritePairings, nextFavoritePairings };
    },
    onError: (err, { data }, context) => {
      const { customerId } = data;

      toast(
        <Toast variant={ToastVariant.Critical}>
          <Warning />
          Unable to complete action. Please try again.
        </Toast>
      );

      queryClient.setQueryData(
        getFavoritePairingsQueryKey(customerId),
        context?.previousFavoritePairings
      );
    },
    onSettled: (data, err, variables) => {
      const filterParam = 'is_favorite=1';

      invalidateFavoritePairings(queryClient, {
        customerId: variables.data.customerId,
      });
      queryClient.invalidateQueries({
        queryKey: getFavoritePairingsQueryKey(variables.data.customerId),
      });

      // Invalidate pairing filter queries that include favorites
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'PairingsV2' &&
          typeof query.queryKey[1] === 'string' &&
          query.queryKey[1].includes(filterParam),
      });
    },
  });
}
